import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Building2, Smartphone, Monitor, Eye, RotateCcw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import VecinoFlow from '@/components/demo/VecinoFlow';
import AdminFlow from '@/components/demo/AdminFlow';
import PresidenteFlow from '@/components/demo/PresidenteFlow';

export default function Demo() {
  const { t, lang, toggle } = useLanguage();
  const [persona, setPersona] = useState(null);

  return (
    <div className="min-h-screen bg-app" data-testid="demo-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#F9F8F6]/85 backdrop-blur-md border-b border-app">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="demo-back-home">
            <span className="w-9 h-9 rounded-md bg-primary-app flex items-center justify-center text-white">
              <Building2 size={18} />
            </span>
            <span className="font-serif text-2xl font-semibold text-app">PortalFinca</span>
          </Link>

          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs bg-[#D4A373]/15 text-[#8B5E2C] px-2.5 py-1 rounded-full font-medium">
              <span className="w-1.5 h-1.5 bg-[#D4A373] rounded-full animate-pulse" />
              {t.demo.mockBadge}
            </span>
            <button
              onClick={toggle}
              className="text-xs font-medium tracking-wider text-app-secondary hover:text-app border border-app rounded-full px-3 py-1.5 transition-colors"
              data-testid="demo-lang-toggle"
            >
              <span className={lang === 'es' ? 'text-app font-semibold' : ''}>ES</span>
              <span className="text-app-muted mx-1">·</span>
              <span className={lang === 'ca' ? 'text-app font-semibold' : ''}>CA</span>
            </button>
            {persona && (
              <button
                onClick={() => setPersona(null)}
                className="inline-flex items-center gap-1.5 text-sm text-app-secondary hover:text-app border border-app rounded-md px-3 py-1.5 transition-colors"
                data-testid="demo-restart"
              >
                <RotateCcw size={14} /> {t.demo.restart}
              </button>
            )}
          </div>
        </div>
      </header>

      <main>
        <AnimatePresence mode="wait">
          {!persona && (
            <motion.div
              key="picker"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="max-w-6xl mx-auto px-6 lg:px-10 py-16 md:py-24"
            >
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-app-secondary hover:text-app mb-12 transition-colors"
                data-testid="back-to-landing"
              >
                <ArrowLeft size={15} /> {t.demo.back}
              </Link>
              <span className="label-eyebrow block mb-4">PortalFinca · Demo</span>
              <h1 className="font-serif text-4xl md:text-6xl leading-tight tracking-tight text-app mb-4">
                {t.demo.pickPersona}
              </h1>
              <p className="text-lg text-app-secondary max-w-xl mb-14">{t.demo.pickPersonaSub}</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <PersonaCard
                  icon={Smartphone}
                  title={t.demo.personaVecino}
                  desc={t.demo.personaVecinoDesc}
                  color="#B34E3A"
                  onClick={() => setPersona('vecino')}
                  testid="persona-vecino"
                />
                <PersonaCard
                  icon={Monitor}
                  title={t.demo.personaAdmin}
                  desc={t.demo.personaAdminDesc}
                  color="#5B6E56"
                  onClick={() => setPersona('admin')}
                  testid="persona-admin"
                  featured
                />
                <PersonaCard
                  icon={Eye}
                  title={t.demo.personaPresidente}
                  desc={t.demo.personaPresidenteDesc}
                  color="#2B6CB0"
                  onClick={() => setPersona('presidente')}
                  testid="persona-presidente"
                />
              </div>
            </motion.div>
          )}

          {persona === 'vecino' && (
            <motion.div
              key="vecino"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <VecinoFlow onRestart={() => setPersona(null)} />
            </motion.div>
          )}
          {persona === 'admin' && (
            <motion.div
              key="admin"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <AdminFlow />
            </motion.div>
          )}
          {persona === 'presidente' && (
            <motion.div
              key="presidente"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <PresidenteFlow />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

function PersonaCard({ icon: Icon, title, desc, color, onClick, testid, featured }) {
  return (
    <motion.button
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 300 }}
      onClick={onClick}
      className={`text-left bg-surface border rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow ${
        featured ? 'border-primary-app/30 ring-1 ring-primary-app/20' : 'border-app'
      }`}
      data-testid={testid}
    >
      <div
        className="w-12 h-12 rounded-md flex items-center justify-center mb-6"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={22} style={{ color }} />
      </div>
      <h3 className="font-serif text-2xl text-app mb-2">{title}</h3>
      <p className="text-app-secondary text-sm leading-relaxed">{desc}</p>
    </motion.button>
  );
}
